// Zeiterfassungs-App der Pille – reine Ableitungen aus echten Stempeln (Handoff Pille §5, Screen 13).
import { stundenText, tagVon, type Stempel } from '../zeiterfassung/berechnung';

export type EreignisArt = 'kommen' | 'pause' | 'weiter' | 'gehen';
export interface Tagesereignis { art: EreignisArt; zeit: string }

/** Kommen/Pause/Weiter/Gehen des Tages in zeitlicher Reihenfolge (mehrere Abschnitte möglich). */
export function tagesereignisse(stempel: readonly Stempel[]): Tagesereignis[] {
  const liste: Tagesereignis[] = [];
  for (const s of stempel) {
    liste.push({ art: 'kommen', zeit: s.beginn });
    for (const p of s.pausen ?? []) {
      liste.push({ art: 'pause', zeit: p.beginn });
      if (p.ende) liste.push({ art: 'weiter', zeit: p.ende });
    }
    if (s.ende) liste.push({ art: 'gehen', zeit: s.ende });
  }
  return liste.sort((a, b) => new Date(a.zeit).getTime() - new Date(b.zeit).getTime());
}

/** Montag bis Sonntag der Woche von `jetzt` als Tagesschlüssel (YYYY-MM-DD, lokale Zeit). */
export function wochenTage(jetzt: Date): string[] {
  const montag = new Date(jetzt.getFullYear(), jetzt.getMonth(), jetzt.getDate());
  montag.setDate(montag.getDate() - ((montag.getDay() + 6) % 7));
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(montag);
    d.setDate(montag.getDate() + i);
    return tagVon(d.toISOString());
  });
}

const KUERZEL = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

export interface WochenBalken {
  tag: string;
  kuerzel: string;
  istMinuten: number;
  /** Höhe 4…100 % */
  hoehe: number;
  status: 'vergangen' | 'heute' | 'kommend';
  /** „8:12“ bzw. „–“ */
  wert: string;
}

/**
 * Balken Mo–Fr, Sa/So nur wenn dort gearbeitet wurde oder Soll anfällt.
 * Maßstab: größter Wert, mindestens 10 h (wie Prototyp).
 */
export function wochenBalken(zeilen: readonly { tag: string; istMinuten: number; sollMinuten: number }[], heute: string): WochenBalken[] {
  const sichtbar = zeilen.filter((z, i) => i < 5 || z.istMinuten > 0 || z.sollMinuten > 0);
  const max = Math.max(600, ...sichtbar.map(z => z.istMinuten));
  return sichtbar.map(z => ({
    tag: z.tag,
    kuerzel: KUERZEL[zeilen.indexOf(z)] ?? '',
    istMinuten: z.istMinuten,
    hoehe: Math.max(4, Math.round((z.istMinuten / max) * 100)),
    status: z.tag === heute ? 'heute' : z.tag < heute ? 'vergangen' : 'kommend',
    wert: z.istMinuten > 0 ? stundenText(z.istMinuten).replace(' h', '') : '–',
  }));
}

/** Zeile unter der großen Uhr: „noch 3:12 h“, „+0:20 h über Soll“ oder „Soll erreicht“. */
export function zeitRest(istMinuten: number, sollMinuten: number): { text: string; ueber: boolean } {
  if (istMinuten > sollMinuten) return { text: `+${stundenText(istMinuten - sollMinuten)} über Soll`, ueber: true };
  if (istMinuten === sollMinuten) return { text: 'Soll erreicht', ueber: false };
  return { text: `noch ${stundenText(sollMinuten - istMinuten)}`, ueber: false };
}

/** Hinweis, wenn Kommen/Pause/Weiter/Gehen nicht gespeichert werden konnte. */
export function stempelFehlerText(art: EreignisArt): string {
  const was = { kommen: 'Kommen', pause: 'Pause', weiter: 'Weiter', gehen: 'Gehen' }[art];
  return `${was} konnte nicht gespeichert werden – bitte erneut versuchen.`;
}

/** Kurzfassung fürs Zeit-Widget (eine Zeile). */
export function stempelFehlerKurz(art: EreignisArt): string {
  return `${{ kommen: 'Kommen', pause: 'Pause', weiter: 'Weiter', gehen: 'Gehen' }[art]} fehlgeschlagen`;
}
